import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';

const MembershipAlertsManagement = () => {
  const navigate = useNavigate();
  const { user, currentTenant, isGymOwner, isSuperAdmin, loading: authLoading } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterResolved, setFilterResolved] = useState('unresolved');
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || (!isGymOwner && !isSuperAdmin))) {
      navigate('/member-dashboard');
    }
  }, [user, isGymOwner, isSuperAdmin, authLoading, navigate]);

  useEffect(() => {
    if (currentTenant?.id && (isGymOwner || isSuperAdmin)) {
      loadAlerts();
    }
  }, [currentTenant, isGymOwner, isSuperAdmin, filterPriority, filterResolved]);

  const loadAlerts = async () => {
    setLoading(true);
    setError('');
    try {
      const filters = {};
      if (filterPriority !== 'all') filters.priority = filterPriority;
      if (filterResolved === 'resolved') filters.isResolved = true;
      if (filterResolved === 'unresolved') filters.isResolved = false;

      const data = await paymentService?.getMembershipAlerts(currentTenant?.id, filters);
      setAlerts(data);
    } catch (err) {
      setError(err?.message || 'Failed to load alerts');
      console.error('Load alerts error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (alertId) => {
    try {
      await paymentService?.markAlertAsRead(alertId);
      loadAlerts();
    } catch (err) {
      setError(err?.message || 'Failed to mark alert as read');
      console.error('Mark as read error:', err);
    }
  };

  const handleResolve = async () => {
    if (!selectedAlert) return;

    try {
      await paymentService?.resolveAlert(selectedAlert?.id, user?.id, resolutionNotes);
      setSuccess('Alert resolved successfully');
      setShowResolveModal(false);
      setSelectedAlert(null);
      setResolutionNotes('');
      loadAlerts();
    } catch (err) {
      setError(err?.message || 'Failed to resolve alert');
      console.error('Resolve alert error:', err);
    }
  };

  const getAlertIcon = (type) => {
    const icons = {
      payment_failed: 'XCircle',
      subscription_expiring: 'Clock',
      payment_overdue: 'AlertTriangle',
      subscription_cancelled: 'UserX',
      payment_succeeded: 'CheckCircle',
      low_balance: 'DollarSign'
    };
    return icons?.[type] || 'Bell';
  };

  const getAlertColor = (priority) => {
    const colors = {
      critical: 'text-destructive',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[priority] || 'text-foreground';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      critical: 'bg-destructive text-destructive-foreground',
      high: 'bg-warning text-warning-foreground',
      medium: 'bg-primary text-primary-foreground',
      low: 'bg-muted text-muted-foreground'
    };
    return badges?.[priority] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Icon name="Loader2" className="w-8 h-8 animate-spin text-primary" />
        </div>
        <MobileBottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24">
        <div className="mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/membership-management-dashboard')}
            iconName="ArrowLeft"
            iconPosition="left"
            className="mb-4"
          >
            Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Membership Alerts</h1>
              <p className="text-muted-foreground">Monitor and manage payment and subscription alerts</p>
            </div>
            <Button onClick={loadAlerts} variant="outline" iconName="RefreshCw" iconPosition="left">
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
            <p className="text-success text-sm">{success}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Priority</label>
            <Select value={filterPriority} onChange={(e) => setFilterPriority(e?.target?.value)}>
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <Select value={filterResolved} onChange={(e) => setFilterResolved(e?.target?.value)}>
              <option value="all">All Alerts</option>
              <option value="unresolved">Unresolved</option>
              <option value="resolved">Resolved</option>
            </Select>
          </div>
        </div>

        <div className="space-y-4">
          {alerts?.map((alert) => (
            <div
              key={alert?.id}
              className={`bg-card rounded-xl shadow-md p-6 border-2 transition-smooth ${
                !alert?.is_read ? 'border-primary' : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    alert?.priority === 'critical' ? 'bg-destructive/10' :
                    alert?.priority === 'high' ? 'bg-warning/10' :
                    alert?.priority === 'medium' ? 'bg-primary/10' : 'bg-muted'
                  }`}>
                    <Icon
                      name={getAlertIcon(alert?.alert_type)}
                      size={24}
                      className={getAlertColor(alert?.priority)}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{alert?.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(alert?.priority)}`}>
                        {alert?.priority}
                      </span>
                      {!alert?.is_read && (
                        <span className="px-2 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                          New
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{alert?.message}</p>
                    {alert?.user_profiles && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="User" size={14} className="text-muted-foreground" />
                        <span className="text-foreground">{alert?.user_profiles?.full_name}</span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-muted-foreground">{alert?.user_profiles?.email}</span>
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(alert?.created_at)?.toLocaleString()}
                    </p>
                  </div>
                </div>

                {alert?.is_resolved && (
                  <div className="px-3 py-1 rounded-full bg-success/10 text-success text-xs font-medium">
                    Resolved
                  </div>
                )}
              </div>

              {!alert?.is_resolved && (
                <div className="flex gap-2">
                  {!alert?.is_read && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleMarkAsRead(alert?.id)}
                    >
                      Mark as Read
                    </Button>
                  )}
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      setSelectedAlert(alert);
                      setShowResolveModal(true);
                    }}
                    iconName="Check"
                    iconPosition="left"
                  >
                    Resolve
                  </Button>
                </div>
              )}

              {alert?.is_resolved && alert?.resolution_notes && (
                <div className="mt-4 p-3 bg-success/5 border border-success/20 rounded-lg">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Resolution: </span>
                    {alert?.resolution_notes}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Resolved on {new Date(alert?.resolved_at)?.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          ))}

          {alerts?.length === 0 && (
            <div className="bg-card rounded-xl shadow-md p-12 text-center">
              <Icon name="Bell" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Alerts Found</h3>
              <p className="text-muted-foreground">
                {filterResolved === 'unresolved' ? 'All alerts have been resolved' : 'No alerts match your filters'}
              </p>
            </div>
          )}
        </div>

        {showResolveModal && selectedAlert && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-lg w-full">
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-foreground">Resolve Alert</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowResolveModal(false);
                      setSelectedAlert(null);
                      setResolutionNotes('');
                    }}
                  >
                    <Icon name="X" size={20} />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-foreground mb-2">{selectedAlert?.title}</h3>
                  <p className="text-sm text-muted-foreground">{selectedAlert?.message}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Resolution Notes
                  </label>
                  <textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e?.target?.value)}
                    placeholder="Describe how this alert was resolved..."
                    rows={4}
                    className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowResolveModal(false);
                      setSelectedAlert(null);
                      setResolutionNotes('');
                    }}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleResolve}
                    iconName="Check"
                    iconPosition="left"
                    fullWidth
                  >
                    Mark as Resolved
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
      <MobileBottomNav />
    </div>
  );
};

export default MembershipAlertsManagement;
